<template>
<div class="crypto-tool">
  <div class="ct-tabs">
    <span v-for="t in tabs" :key="t.key" class="ct-tab" :class="{ on: tab===t.key }" @click="tab=t.key">
      <i :class="t.icon" />{{ t.label }}
    </span>
  </div>

  <!-- AES -->
  <div v-if="tab==='aes'" class="ct-panel">
    <div class="ct-card">
      <div class="ct-row">
        <span class="ct-lbl">{{ $t('tool.CryptoTool.aesMode') }}</span>
        <el-radio-group v-model="aes.mode" size="small"><el-radio-button label="CBC">CBC</el-radio-button><el-radio-button label="GCM">GCM</el-radio-button></el-radio-group>
        <span class="ct-lbl">{{ $t('tool.CryptoTool.aesKey') }}</span>
        <el-radio-group v-model="aes.bits" size="small"><el-radio-button :label="128">128</el-radio-button><el-radio-button :label="256">256</el-radio-button></el-radio-group>
        <el-button size="small" @click="genAesKey" icon="el-icon-refresh">{{ $t('tool.CryptoTool.genKey') }}</el-button>
      </div>
      <div class="ct-row">
        <span class="ct-lbl">{{ $t('tool.CryptoTool.aesKey') }}</span>
        <el-input v-model="aes.key" size="small" :placeholder="$t('tool.CryptoTool.phHexBits',{n:aes.bits===128?32:64})" class="ct-mono ct-flex" />
      </div>
      <div class="ct-row" v-if="aes.mode==='GCM'">
        <span class="ct-lbl">{{ $t('tool.CryptoTool.aesIv') }}</span>
        <el-input v-model="aes.iv" size="small" :placeholder="$t('tool.CryptoTool.phHexBits',{n:24})" class="ct-mono ct-flex" />
      </div>
    </div>
    <div class="ct-card">
      <div class="ct-grid">
        <div><div class="ct-hd">{{ $t('tool.CryptoTool.plaintext') }}</div><el-input v-model="aes.plain" type="textarea" :rows="6" :placeholder="$t('tool.CryptoTool.phPlaintext')" class="ct-mono" /></div>
        <div><div class="ct-hd">{{ $t('tool.CryptoTool.ciphertextBase64') }}</div><el-input v-model="aes.cipher" type="textarea" :rows="6" readonly :placeholder="$t('tool.CryptoTool.phCiphertext')" class="ct-mono ct-out" /></div>
      </div>
      <div class="ct-acts">
        <el-button type="primary" size="small" @click="aesEncrypt" :loading="aes.loading" icon="el-icon-lock">{{ $t('tool.CryptoTool.aesEncrypt') }}</el-button>
        <el-button size="small" @click="aesDecrypt" icon="el-icon-unlock">{{ $t('tool.CryptoTool.aesDecrypt') }}</el-button>
        <span class="ct-err" v-if="aes.err"><i class="el-icon-warning" />{{ aes.err }}</span>
      </div>
    </div>
  </div>

  <!-- Hash -->
  <div v-if="tab==='hash'" class="ct-panel">
    <div class="ct-card">
      <div class="ct-row">
        <span class="ct-lbl">{{ $t('tool.CryptoTool.hashAlgo') }}</span>
        <el-radio-group v-model="hash.algo" size="small">
          <el-radio-button label="SHA-256">SHA-256</el-radio-button>
          <el-radio-button label="SHA-512">SHA-512</el-radio-button>
          <el-radio-button label="SHA-1">SHA-1</el-radio-button>
          <el-radio-button label="MD5">MD5</el-radio-button>
        </el-radio-group>
      </div>
      <el-input v-model="hash.input" type="textarea" :rows="4" :placeholder="$t('tool.CryptoTool.phInput')" class="ct-mono" @input="hashOutput=''" />
      <div class="ct-acts">
        <el-button type="primary" size="small" @click="doHash" icon="el-icon-s-data">{{ $t('tool.CryptoTool.computeHash') }}</el-button>
        <el-checkbox v-model="hash.upper" size="small">{{ $t('tool.CryptoTool.upper') }}</el-checkbox>
      </div>
    </div>
    <div class="ct-result" v-if="hashOutput">
      <div class="ct-result-hd">{{ hash.algo }}</div>
      <code class="ct-result-code" @click="copy(hashOutput)">{{ hashOutput }}</code>
    </div>
  </div>

  <!-- HMAC -->
  <div v-if="tab==='hmac'" class="ct-panel">
    <div class="ct-card">
      <div class="ct-row">
        <span class="ct-lbl">{{ $t('tool.CryptoTool.hashAlgo') }}</span>
        <el-radio-group v-model="hmac.algo" size="small">
          <el-radio-button label="SHA-256">HMAC-SHA256</el-radio-button>
          <el-radio-button label="SHA-512">HMAC-SHA512</el-radio-button>
        </el-radio-group>
      </div>
      <div class="ct-row">
        <span class="ct-lbl">{{ $t('tool.CryptoTool.aesKey') }}</span>
        <el-input v-model="hmac.key" size="small" :placeholder="$t('tool.CryptoTool.phKey')" class="ct-mono ct-flex" />
        <el-button size="small" @click="hmac.key=randHex(32)">{{ $t('tool.CryptoTool.randomKey') }}</el-button>
      </div>
      <el-input v-model="hmac.msg" type="textarea" :rows="3" :placeholder="$t('tool.CryptoTool.phMsg')" class="ct-mono" @input="hmacOut=''" />
      <div class="ct-acts">
        <el-button type="primary" size="small" @click="doHmac" icon="el-icon-s-data">{{ $t('tool.CryptoTool.computeHmac') }}</el-button>
      </div>
    </div>
    <div class="ct-result" v-if="hmacOut">
      <div class="ct-result-hd">HMAC — {{ hmac.algo }}</div>
      <code class="ct-result-code" @click="copy(hmacOut)">{{ hmacOut }}</code>
    </div>
  </div>

  <!-- Encode -->
  <div v-if="tab==='encode'" class="ct-panel">
    <div class="ct-card">
      <div class="ct-row">
        <span class="ct-lbl">{{ $t('tool.CryptoTool.encType') }}</span>
        <el-radio-group v-model="enc.type" size="small" @change="doEncode">
          <el-radio-button label="base64">Base64</el-radio-button>
          <el-radio-button label="hex">Hex</el-radio-button>
          <el-radio-button label="url">URL</el-radio-button>
        </el-radio-group>
      </div>
      <div class="ct-grid">
        <div><div class="ct-hd">{{ enc.type==='url' ? $t('tool.CryptoTool.raw') : $t('tool.CryptoTool.text') }}</div><el-input v-model="enc.plain" type="textarea" :rows="5" :placeholder="$t('tool.CryptoTool.phInput')" class="ct-mono" @input="doEncode" /></div>
        <div><div class="ct-hd">{{ enc.type==='url' ? $t('tool.CryptoTool.encoded') : enc.type.toUpperCase() === 'BASE64' ? 'Base64' : 'Hex' }}</div><el-input v-model="enc.encoded" type="textarea" :rows="5" :placeholder="$t('tool.CryptoTool.phResult')" class="ct-mono ct-out" @input="doDecode" /></div>
      </div>
    </div>
  </div>

  <!-- RSA -->
  <div v-if="tab==='rsa'" class="ct-panel">
    <div class="ct-card">
      <div class="ct-acts">
        <el-button type="primary" size="small" @click="genRsaKey" :loading="rsa.genLoading" icon="el-icon-key">{{ $t('tool.CryptoTool.genRsaKey') }}</el-button>
        <span v-if="rsa.generated" class="ct-ok"><i class="el-icon-success" />{{ $t('tool.CryptoTool.ready') }}</span>
      </div>
      <div class="ct-row"><span class="ct-lbl">{{ $t('tool.CryptoTool.rsaPubKey') }}</span><el-input v-model="rsa.pubKey" type="textarea" :rows="2" readonly :placeholder="$t('tool.CryptoTool.phResult')" class="ct-mono ct-flex" /><el-button size="small" @click="copy(rsa.pubKey)">{{ $t("common.action.copy") }}</el-button></div>
      <div class="ct-row"><span class="ct-lbl">{{ $t('tool.CryptoTool.rsaPrivKey') }}</span><el-input v-model="rsa.priKey" type="textarea" :rows="2" readonly :placeholder="$t('tool.CryptoTool.phResult')" class="ct-mono ct-flex" /><el-button size="small" @click="copy(rsa.priKey)">{{ $t("common.action.copy") }}</el-button></div>
      <div class="ct-grid">
        <div><div class="ct-hd">{{ $t('tool.CryptoTool.plaintext') }}</div><el-input v-model="rsa.plain" type="textarea" :rows="4" :placeholder="$t('tool.CryptoTool.phPlaintext')" /></div>
        <div><div class="ct-hd">{{ $t('tool.CryptoTool.ciphertextBase64') }}</div><el-input v-model="rsa.cipher" type="textarea" :rows="4" readonly :placeholder="$t('tool.CryptoTool.phCiphertext')" class="ct-mono ct-out" /></div>
      </div>
      <div class="ct-acts">
        <el-button type="primary" size="small" @click="rsaEncrypt" icon="el-icon-lock">{{ $t('tool.CryptoTool.rsaEncrypt') }}</el-button>
        <el-button size="small" @click="rsaDecrypt" icon="el-icon-unlock">{{ $t('tool.CryptoTool.rsaDecrypt') }}</el-button>
        <span class="ct-err" v-if="rsa.err"><i class="el-icon-warning" />{{ rsa.err }}</span>
      </div>
    </div>
  </div>
</div>
</template>

<script>
function md5(str){function r(n,c){return(n<<c)|(n>>>(32-c))}function q(x){var a=(x&0xFFFF)+(x>>16);return a+(a>>16)}var h=[0x67452301,0xEFCDAB89,0x98BADCFE,0x10325476],k,x=[],i,j,l=str.length;str=unescape(encodeURIComponent(str));for(i=0;i<l;i++)x[i>>2]|=(str.charCodeAt(i)&0xFF)<<((i%4)*8);x[i>>2]|=0x80<<((i%4)*8);x[((l+8)>>6)*16+14]=l*8;for(var b=0;b<x.length;b+=16){var a=h[0],c=h[1],d=h[2],e=h[3];for(i=0;i<64;i++){if(i<16){k=x[b+i];var f=(c&d)|((~c)&e);var g=i}else if(i<32){k=x[b+((i*5)+1)%16];f=(c&e)|(d&(~e));g=5*i+1}else if(i<48){k=x[b+((i*3)+5)%16];f=c^d^e;g=3*i+5}else{k=x[b+(i*7)%16];f=d^(c|(~e));g=7*i}var t=e;e=d;d=c;c=c+r(a+f+k+[0xD76AA478,0xE8C7B756,0x242070DB,0xC1BDCEEE,0xF57C0FAF,0x4787C62A,0xA8304613,0xFD469501,0x698098D8,0x8B44F7AF,0xFFFF5BB1,0x895CD7BE,0x6B901122,0xFD987193,0xA679438E,0x49B40821,0xF61E2562,0xC040B340,0x265E5A51,0xE9B6C7AA,0xD62F105D,0x02441453,0xD8A1E681,0xE7D3FBC8,0x21E1CDE6,0xC33707D6,0xF4D50D87,0x455A14ED,0xA9E3E905,0xFCEFA3F8,0x676F02D9,0x8D2A4C8A,0xFFFA3942,0x8771F681,0x6D9D6122,0xFDE5380C,0xA4BEEA44,0x4BDECFA9,0xF6BB4B60,0xBEBFBC70,0x289B7EC6,0xEAA127FA,0xD4EF3085,0x04881D05,0xD9D4D039,0xE6DB99E5,0x1FA27CF8,0xC4AC5665,0xF4292244,0x432AFF97,0xAB9423A7,0xFC93A039,0x655B59C3,0x8F0CCC92,0xFFEFF47D,0x85845dd1,0x6FA87E4F,0xFE2CE6E0,0xA3014314,0x4E0811A1,0xF7537E82,0xBD3AF235,0x2AD7D2BB,0xEB86D391][i],r(i%32<16?7:i%32<32?12:i%32<48?17:22));a=t}h[0]=q(h[0]+a);h[1]=q(h[1]+c);h[2]=q(h[2]+d);h[3]=q(h[3]+e)}k='';for(i=0;i<4;i++)for(j=0;j<4;j++)k+=((h[i]>>>(j*8))&0xFF).toString(16).padStart(2,'0');return k}
export default {
  name:'CryptoTool',props:{config:{type:Object,default:()=>({})}},
  data:()=>({
    tab:'aes',
    aes:{mode:'GCM',bits:256,key:'',iv:'',plain:'',cipher:'',loading:false,err:''},
    hash:{algo:'SHA-256',input:'',upper:false},hmac:{algo:'SHA-256',key:'',msg:''},
    enc:{type:'base64',plain:'',encoded:''},rsa:{pubKey:'',priKey:'',plain:'',cipher:'',genLoading:false,generated:false,err:'',keyPair:null},
    hashOutput:'',hmacOut:''
  }),
  computed:{
    tabs:function(){var t=this;return[
      {key:'aes',label:t.$t('tool.CryptoTool.modeAES'),icon:'el-icon-lock'},
      {key:'hash',label:t.$t('tool.CryptoTool.modeHash'),icon:'el-icon-s-data'},
      {key:'hmac',label:t.$t('tool.CryptoTool.modeHMAC'),icon:'el-icon-s-check'},
      {key:'encode',label:t.$t('tool.CryptoTool.modeEncode'),icon:'el-icon-sort'},
      {key:'rsa',label:t.$t('tool.CryptoTool.modeRSA'),icon:'el-icon-key'}]}
  },
  methods:{
    randHex(l){return Array.from(crypto.getRandomValues(new Uint8Array(l/2)),b=>b.toString(16).padStart(2,'0')).join('')},
    hex2buf(h){return new Uint8Array(h.match(/.{1,2}/g).map(b=>parseInt(b,16))).buffer},
    buf2hex(b){return Array.from(new Uint8Array(b),x=>x.toString(16).padStart(2,'0')).join('')},
    str2buf(s){return new TextEncoder().encode(s)},
    async genAesKey(){this.aes.key=this.randHex(this.aes.bits/8*2);if(this.aes.mode==='GCM')this.aes.iv=this.randHex(24)},
    async aesEncrypt(){this.aes.err='';this.aes.loading=true;try{const k=await crypto.subtle.importKey('raw',this.hex2buf(this.aes.key),{name:'AES-'+this.aes.mode},false,['encrypt']);const a=this.aes.mode==='GCM'?{name:'AES-GCM',iv:this.hex2buf(this.aes.iv)}:{name:'AES-CBC',iv:this.hex2buf(this.aes.iv||'00000000000000000000000000000000')};const e=await crypto.subtle.encrypt(a,k,this.str2buf(this.aes.plain));this.aes.cipher=btoa(String.fromCharCode(...new Uint8Array(e)))}catch(e){this.aes.err=this.$t('tool.CryptoTool.errEncrypt')+': '+e.message};this.aes.loading=false},
    async aesDecrypt(){this.aes.err='';try{const k=await crypto.subtle.importKey('raw',this.hex2buf(this.aes.key),{name:'AES-'+this.aes.mode},false,['decrypt']);const a=this.aes.mode==='GCM'?{name:'AES-GCM',iv:this.hex2buf(this.aes.iv)}:{name:'AES-CBC',iv:this.hex2buf(this.aes.iv||'00000000000000000000000000000000')};const d=Uint8Array.from(atob(this.aes.cipher),c=>c.charCodeAt(0));const r=await crypto.subtle.decrypt(a,k,d);this.aes.plain=new TextDecoder().decode(r)}catch(e){this.aes.err=this.$t('tool.CryptoTool.errDecrypt')+': '+e.message}},
    async doHash(){this.hashOutput='';const d=this.str2buf(this.hash.input);if(this.hash.algo==='MD5'){this.hashOutput=md5(this.hash.input)}else{const h=await crypto.subtle.digest(this.hash.algo,d);this.hashOutput=this.buf2hex(h)};if(this.hash.upper)this.hashOutput=this.hashOutput.toUpperCase()},
    async doHmac(){this.hmacOut='';const k=await crypto.subtle.importKey('raw',this.str2buf(this.hmac.key),{name:'HMAC',hash:this.hmac.algo},false,['sign']);const s=await crypto.subtle.sign('HMAC',k,this.str2buf(this.hmac.msg));this.hmacOut=this.buf2hex(s)},
    doEncode(){try{if(this.enc.type==='base64')this.enc.encoded=btoa(unescape(encodeURIComponent(this.enc.plain)));else if(this.enc.type==='hex')this.enc.encoded=Array.from(new TextEncoder().encode(this.enc.plain),b=>b.toString(16).padStart(2,'0')).join('');else this.enc.encoded=encodeURIComponent(this.enc.plain)}catch(e){}},
    doDecode(){try{if(this.enc.type==='base64')this.enc.plain=decodeURIComponent(escape(atob(this.enc.encoded)));else if(this.enc.type==='hex')this.enc.plain=new TextDecoder().decode(new Uint8Array(this.enc.encoded.match(/.{1,2}/g).map(b=>parseInt(b,16))));else this.enc.plain=decodeURIComponent(this.enc.encoded)}catch(e){}},
    async genRsaKey(){this.rsa.genLoading=true;try{const p=await crypto.subtle.generateKey({name:'RSA-OAEP',modulusLength:2048,publicExponent:new Uint8Array([1,0,1]),hash:'SHA-256'},true,['encrypt','decrypt']);this.rsa.keyPair=p;const pub=await crypto.subtle.exportKey('spki',p.publicKey);const pri=await crypto.subtle.exportKey('pkcs8',p.privateKey);this.rsa.pubKey=btoa(String.fromCharCode(...new Uint8Array(pub)));this.rsa.priKey=btoa(String.fromCharCode(...new Uint8Array(pri)));this.rsa.generated=true}catch(e){this.rsa.err=this.$t('tool.CryptoTool.errGen')+': '+e.message};this.rsa.genLoading=false},
    async rsaEncrypt(){this.rsa.err='';if(!this.rsa.keyPair){this.rsa.err=this.$t('tool.CryptoTool.errNeedKey');return};try{const e=await crypto.subtle.encrypt('RSA-OAEP',this.rsa.keyPair.publicKey,this.str2buf(this.rsa.plain));this.rsa.cipher=btoa(String.fromCharCode(...new Uint8Array(e)))}catch(e){this.rsa.err=this.$t('tool.CryptoTool.errEncrypt')+': '+e.message}},
    async rsaDecrypt(){this.rsa.err='';if(!this.rsa.keyPair){this.rsa.err=this.$t('tool.CryptoTool.errNeedKey');return};try{const d=Uint8Array.from(atob(this.rsa.cipher),c=>c.charCodeAt(0));const r=await crypto.subtle.decrypt('RSA-OAEP',this.rsa.keyPair.privateKey,d);this.rsa.plain=new TextDecoder().decode(r)}catch(e){this.rsa.err=this.$t('tool.CryptoTool.errDecrypt')+': '+e.message}},
    copy(t){navigator.clipboard.writeText(t);this.$message.success(this.$t("common.status.copied"))},
    doAction(a){if(a==='clear'){Object.assign(this.$data,this.$options.data())}}
  }
}
</script>

<style scoped>
.crypto-tool { width:100%; }

/* tabs */
.ct-tabs { display:flex; gap:4px; margin-bottom:20px; background:#f5f6f8; border-radius:10px; padding:4px; }
.ct-tab { flex:1; text-align:center; padding:9px 0; font-size:13px; cursor:pointer; color:#8892a6; border-radius:8px; transition:all .2s; }
.ct-tab:hover { color:#4e7cff; }
.ct-tab.on { background:#fff; color:#4e7cff; font-weight:600; box-shadow:0 1px 3px rgba(0,0,0,.06); }
.ct-tab i { margin-right:4px; }

/* card */
.ct-panel { display:flex; flex-direction:column; gap:12px; }
.ct-card { background:#fff; border-radius:12px; padding:20px 24px; border:1px solid #ebeef5; }

/* row */
.ct-row { display:flex; align-items:center; gap:10px; margin-bottom:10px; }
.ct-lbl { font-size:12px; color:#8892a6; font-weight:600; width:36px; flex-shrink:0; text-align:right; }
.ct-flex { flex:1; }

/* grid */
.ct-grid { display:flex; gap:14px; }
.ct-grid > div { flex:1; min-width:0; }
.ct-hd { font-size:12px; font-weight:600; color:#8892a6; margin-bottom:6px; }

/* actions */
.ct-acts { display:flex; align-items:center; gap:8px; margin-top:10px; margin-bottom:10px; }
.ct-err { font-size:12px; color:#f56c6c; display:flex; align-items:center; gap:3px; }
.ct-ok { font-size:12px; color:#67c23a; display:flex; align-items:center; gap:3px; }

/* result */
.ct-result { background:linear-gradient(135deg,#f0f4ff,#f8f9ff); border:1px solid #dce3f8; border-radius:12px; padding:16px 20px; }
.ct-result-hd { font-size:11px; color:#8892a6; font-weight:600; margin-bottom:8px; text-transform:uppercase; letter-spacing:.5px; }
.ct-result-code { display:block; font-size:15px; font-family:monospace; color:#1a1f36; word-break:break-all; cursor:pointer; line-height:1.6; border-radius:6px; }
.ct-result-code:hover { background:rgba(78,124,255,.04); }

/* inputs */
.ct-mono >>> textarea,
.ct-mono >>> input { font-family:'JetBrains Mono','Consolas',monospace !important; font-size:13px !important; }
.ct-out >>> textarea { background:#fafbfc; color:#4a5568; }
.ct-card .el-textarea { margin-bottom:0; }

/* spacing: last child in card should not have bottom margin */
.ct-card > :last-child { margin-bottom:0; }
.ct-row:last-of-type { margin-bottom:0; }

@media (max-width:560px) {
  .ct-grid { flex-direction:column; }
  .ct-tab { font-size:12px; }
  .ct-tab i { display:none; }
  .ct-row { flex-wrap:wrap; }
}
</style>
